import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "src/app/store";
import Cookies from "js-cookie";
import { runtimeConfig } from "src/services/runtimeConfig";
import { mixpanelIdentify, mixpanelReset } from "src/services/mixpanelService";

export interface AuthState {
  acctId: string | null;
  acctName: string | null;
  username: string | null;
  fullName: string | null;
  quickScrub: boolean | null;
  role: string | null;
  isSysadmin: boolean | null;
  hasSalesReportAccess: boolean | null;
  loginId: string | null;
  csrfToken: string | null;
  isAuthenticated: boolean | null;
  isLoading: boolean;
  hasTrainingFeature: boolean;
  homePage: string | null;
  landingPage: string | null;
  acctMessage: string | null;
  theme: string | null;
  userEmail: string | null;
  isEmailVerified: boolean;
  isNXXG: boolean;
  // Absolute session expiry as ISO 8601 UTC. Driven by the server on login and
  // every keepalive. Consumed by useInactivity / App.tsx for wall-clock idle
  // detection that is immune to setTimeout throttling on background tabs.
  expiresAt: string | null;
}

const initialState: AuthState = {
  acctId: null,
  acctName: null,
  username: null,
  fullName: null,
  quickScrub: null,
  role: null,
  isSysadmin: null,
  hasSalesReportAccess: null,
  loginId: null,
  csrfToken: null,
  isAuthenticated: false,
  isLoading: false,
  hasTrainingFeature: false,
  homePage: null,
  landingPage: null,
  acctMessage: null,
  theme: null,
  userEmail: null,
  isEmailVerified: false,
  isNXXG: false,
  expiresAt: null,
};

// Cookie domain resolved from the current hostname (.dncscrub.com / .dnctopco.dev)
// so cookies are shared across that deployment's subdomains. See runtimeConfig.
const domainName: string = runtimeConfig.cookieDomain;

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthInfo: (
      state,
      action: PayloadAction<{
        acctId: string;
        acctName: string;
        username: string;
        fullName: string;
        quickScrub: boolean;
        role: string;
        isSysadmin: boolean;
        hasSalesReportAccess: boolean;
        loginId: string;
        csrfToken: string;
        hasTrainingFeature: boolean;
        homePage: string;
        landingPage: string;
        acctMessage: string;
        userEmail: string;
        isEmailVerified: boolean;
        isNXXG: boolean;
        expiresAt: string | null;
      }>,
    ) => {
      state.acctId = action.payload.acctId.trim();
      state.acctName = action.payload.acctName;
      state.username = action.payload.username.trim();
      state.fullName = action.payload.fullName;
      state.quickScrub = action.payload.quickScrub;
      state.role = action.payload.role;
      state.isSysadmin = action.payload.isSysadmin;
      state.hasSalesReportAccess = action.payload.hasSalesReportAccess;
      state.loginId = action.payload.loginId;
      state.csrfToken = action.payload.csrfToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.hasTrainingFeature = action.payload.hasTrainingFeature;
      state.homePage = action.payload.homePage;
      state.landingPage = action.payload.landingPage;
      state.acctMessage = action.payload.acctMessage;
      state.userEmail = action.payload.userEmail;
      state.isEmailVerified = action.payload.isEmailVerified;
      state.isNXXG = action.payload.isNXXG;
      state.expiresAt = action.payload.expiresAt;

      Cookies.set("acctId", action.payload.acctId.trim(), {
        domain: domainName,
        path: "/",
      });
      // Just let the server send the loginId cookie unless
      // on dev where the server may be at domain https://localhost.
      // See if this eliminates intermittent bug where user is
      // logged into React app but can't use Tcl app
      // - JH 2025-02-15
      const shouldSetLoginIdCookie =
        process.env.NODE_ENV === "development" &&
        runtimeConfig.apiUrl.includes("localhost");
      if (shouldSetLoginIdCookie) {
        Cookies.set("loginId", action.payload.loginId, {
          domain: domainName,
          path: "/",
        });
      }

      // Tie Mixpanel events to this user (same side-effect pattern as the
      // cookies above; no-ops when Mixpanel is disabled, e.g. local dev).
      mixpanelIdentify(
        action.payload.username.trim(),
        action.payload.acctId.trim(),
        {
          acctName: action.payload.acctName,
          role: action.payload.role,
          isSysadmin: action.payload.isSysadmin,
        },
      );
    },
    logout: (state) => {
      state.acctId = null;
      state.acctName = null;
      state.username = null;
      state.fullName = null;
      state.quickScrub = null;
      state.role = null;
      state.isSysadmin = null;
      state.hasSalesReportAccess = null;
      state.loginId = null;
      state.csrfToken = null;
      state.hasTrainingFeature = false;
      state.isAuthenticated = false;
      state.homePage = null;
      state.landingPage = null;
      state.acctMessage = null;
      state.expiresAt = null;

      // Do not remove it for now as this is used to prefil the account id in the login page
      //Cookies.remove("acctId", { domain: domainName, path: "/" });
      Cookies.remove("loginId", { domain: domainName, path: "/" });
      Cookies.remove("csrfToken", { domain: domainName, path: "/" });

      // Drop the Mixpanel identity so a shared browser doesn't chain sessions.
      mixpanelReset();
    },
    startLoading: (state) => {
      state.isLoading = true;
    },
    setUnauthenticated: (state) => {
      state.isAuthenticated = false;
      state.isLoading = false;
    },
    setIsAuthenticated: (state) => {
      state.isAuthenticated = true;
    },
    updateAccountInfo: (
      state,
      action: PayloadAction<{ acctId: string; acctName: string }>,
    ) => {
      state.acctId = action.payload.acctId.trim();
      state.acctName = action.payload.acctName;
    },
    setTheme: (state, action: PayloadAction<string>) => {
      state.theme = action.payload;
    },
    // Keepalive responses update expiresAt so the idle hook and modal countdown
    // re-sync to the server's authoritative deadline after every refresh.
    updateExpiresAt: (state, action: PayloadAction<string | null>) => {
      state.expiresAt = action.payload;
    },
  },
});

export const selectAuth = (state: RootState) => state.auth;

export const {
  setAuthInfo,
  logout,
  startLoading,
  setUnauthenticated,
  setIsAuthenticated,
  updateAccountInfo,
  setTheme,
  updateExpiresAt,
} = authSlice.actions;

export default authSlice.reducer;
